/**
 * ServiceAuthSSO.js
 *
 * Autenticação multi-tenant via backend (admin-service).
 * Fluxo:
 *   1. Frontend envia {tenant, username, password} ao backend
 *   2. Backend resolve config do tenant no tenant-provider (server-side)
 *   3. Backend faz Direct Access Grant no Keycloak (client_secret nunca sai do backend)
 *   4. Backend retorna access_token + refresh_token (JWT RS256)
 */

import axios from '@/plugins/axios';
import { authStorage } from '@/lib/authStorage';

export default class ServiceAuthSSO {
  /**
   * Lista tenants ativos disponíveis para o seletor de login.
   * Retorna [] se tenant-provider não estiver configurado (on-premise).
   */
  static async listTenants() {
    try {
      const response = await axios.get('auth/tenants');
      return response.data?.tenants ?? [];
    } catch {
      return [];
    }
  }

  /**
   * Login via backend — envia org + credenciais, recebe JWT
   */
  static async login(tenant, username, password) {
    const response = await axios.post(`auth/login`, {
      tenant,
      username,
      password,
    });

    return response.data;
  }

  /**
   * Verify 2FA code via backend
   */
  static async verify2fa(tenant, username, password, sessionToken, code) {
    const response = await axios.post(`auth/verify-2fa`, {
      tenant,
      username,
      password,
      sessionToken,
      code,
    });

    return response.data;
  }

  /**
   * Solicita redefinição de senha (self-service). O backend dispara o email nativo
   * do Keycloak (ação UPDATE_PASSWORD). Resposta é sempre genérica (anti-enumeração):
   * não revela se o e-mail existe. Retorna void.
   */
  static async forgotPassword(tenant, email) {
    await axios.post('auth/forgot-password', { tenant, email });
  }

  /**
   * Refresh token via backend
   */
  static async refreshToken(tenant, refreshToken) {
    const response = await axios.post(`auth/refresh`, {
      tenant,
      refreshToken,
    });

    return response.data;
  }

  /**
   * Logout — invalida sessão no Keycloak via backend
   */
  static async logout(refreshToken) {
    const tenant = authStorage.getItem('tenant_slug');
    if (!tenant || !refreshToken) return;

    try {
      await axios.post(`auth/logout`, {
        tenant,
        refreshToken,
      });
    } catch {
      // best-effort — se falhar, session expira sozinha no KC
    }
  }

  /**
   * Decodifica JWT payload (sem verificar assinatura — backend faz isso)
   */
  static decodeJwtPayload(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }

  /**
   * Extrai tenant do issuer do JWT
   * "https://sso.agnes.com.br/realms/estado-sp-dev" → "estado-sp-dev"
   */
  static extractTenantFromToken(token) {
    const payload = ServiceAuthSSO.decodeJwtPayload(token);
    if (!payload?.iss) return null;

    const realmMatch = payload.iss.match(/\/realms\/(.+)$/);
    return realmMatch ? realmMatch[1] : null;
  }

  /**
   * Extrai roles do JWT Keycloak
   */
  static extractRolesFromToken(token) {
    const payload = ServiceAuthSSO.decodeJwtPayload(token);
    return payload?.realm_access?.roles || [];
  }

  /**
   * Tenta refresh silencioso do token. Retorna novo token response ou null se falhar.
   */
  static async trySilentRefresh() {
    const tenant = authStorage.getItem('tenant_slug');
    const refreshToken = authStorage.getItem('refresh_Token');

    if (!tenant || !refreshToken) return null;

    try {
      const data = await ServiceAuthSSO.refreshToken(tenant, refreshToken);
      ServiceAuthSSO.persistSession(data, tenant);
      return data.access_token;
    } catch (error) {
      const status = error.response?.status ?? 'sem resposta';
      const detail = error.response?.data?.detail ?? error.response?.data?.message ?? error.message;
      // Não engolir o erro: logar contexto para diagnosticar 500 no refresh de token.
      // NÃO logar o objeto `error` cru: ele carrega `config.data` com o refresh_token
      // em texto puro (vazaria a credencial no console). status+detail já bastam.
      console.error(
        `[SSO] Falha no refresh silencioso de token | tenant=${tenant} | status=${status} | detail=${detail}`,
      );
      return null;
    }
  }

  /**
   * Verifica se o auth mode atual é SSO
   */
  static isSSO() {
    return authStorage.getItem('auth_mode') === 'sso';
  }

  /**
   * Extrai user info do JWT (sem chamar backend)
   */
  static getUserFromToken(token) {
    const payload = ServiceAuthSSO.decodeJwtPayload(token);
    if (!payload) return null;

    // KC roles são passadas diretamente — a resolução de permissões
    // é feita pelo backend (PermissionResolvingFilter) e acessada via StoreUserPermissions
    const roles = payload.realm_access?.roles || [];

    return {
      id: payload.sub,
      firstName: payload.given_name || '',
      lastName: payload.family_name || '',
      username: payload.preferred_username || payload.email || '',
      emailAddress: payload.email || '',
      authorities: roles,
      twoFactorAuthenticationEnabled: false,
      thumbnailId: null,
      cellPhone: null,
    };
  }

  /**
   * Persiste a sessão SSO no localStorage a partir da resposta de token do backend.
   * tokenResponse: { access_token, refresh_token }
   */
  static persistSession(tokenResponse, tenant, remember) {
    const { access_token, refresh_token } = tokenResponse || {};
    if (!access_token) throw new Error('resposta de login sem access_token');
    // `remember` só vem no login (checkbox "Manter-me conectado"). No refresh
    // silencioso não vem — aí mantém a sessão no MESMO storage onde já vive:
    // se o token está na sessionStorage => temporária (false); senão localStorage.
    const persist = remember ?? sessionStorage.getItem('access_Token') === null;
    authStorage.setItem('access_Token', access_token, persist);
    if (refresh_token) authStorage.setItem('refresh_Token', refresh_token, persist);
    authStorage.setItem('auth_mode', 'sso', persist);
    if (tenant) authStorage.setItem('tenant_slug', tenant, persist);
    const payload = ServiceAuthSSO.decodeJwtPayload(access_token);
    if (payload?.exp) {
      authStorage.setItem('access_token_expiration_date', String(payload.exp), persist);
    }
  }

  /**
   * Logout completo: invalida a sessão no Keycloak (best-effort, via backend) e
   * limpa todos os dados de sessão do localStorage. O reset dos stores (permissões,
   * user) fica com o caller (evita import circular service↔store).
   */
  static async signOut() {
    const refreshToken = authStorage.getItem('refresh_Token');
    // logout lê tenant_slug internamente — chamar ANTES de limpar. É best-effort (não lança).
    await ServiceAuthSSO.logout(refreshToken);
    [
      'access_Token',
      'refresh_Token',
      'access_token_expiration_date',
      'tenant_slug',
      'auth_mode',
    ].forEach((k) => authStorage.removeItem(k));
  }

  /**
   * Limpa dados SSO do localStorage
   */
  static clearSSOData() {
    authStorage.removeItem('tenant_slug');
    authStorage.removeItem('auth_mode');
  }
}
